import { useCallback, useEffect, useMemo, useState } from 'react';
import { Seletor } from '../Filtros';
import {
  AlternarDaBarra, BarraDeLista, BuscaDaBarra, FiltrosAtivos, ResumoQueFiltra,
  RodapeDaLista, usePaginacao, type FiltroAtivo,
} from '../BarraDeLista';
import { Periodo, type ValorDoPeriodo } from '../Periodo';
import { Link, useParams } from 'react-router-dom';
import { api, mensagemDoErro } from '../api';
import { data as fmtData } from '../formato';
import {
  Aviso, Campo, Carregando, Dialogo, ErroDaApi, Escolha, Selo, Vazio,
} from '../ui';
import type { Tom } from '../ui';
import { useSessao } from '../sessao';
import { useCaminho } from '../Caminho';

/**
 * Não conformidades, trabalho não conforme e reclamações.
 *
 * ── Uma tela, quatro nomes ──────────────────────────────────────────
 *
 * O §17 lista os quatro separados, e a 17025 os organiza em §7.9
 * (reclamação), §7.10 (trabalho não conforme) e §8.7 (o que se faz com
 * qualquer um). São o mesmo registro visto de lugares diferentes, e o
 * CAMINHO é um só: registra, contém, procura a causa, age, confere se
 * funcionou, encerra.
 *
 * ── A escada aparece, e ela não deixa pular degrau ──────────────────
 *
 * A ficha mostra o que FALTA para encerrar — e a frase vem do banco, que
 * é quem recusa. Uma tela que decidisse isso por conta própria ofereceria
 * um botão que o servidor nega, ou esconderia um que ele aceitaria.
 *
 * O degrau que importa é o último: verificar a eficácia. É o que todo
 * sistema da qualidade esquece, porque é o único que acontece semanas
 * depois — quando o problema já saiu da cabeça de todo mundo.
 */

interface Nc {
  id: string;
  numero: string;
  tipo: string;
  origem: string;
  titulo: string;
  descricao: string;
  gravidade: string;
  situacao: string;
  detectada_em: string;
  detectada_por_nome: string | null;
  acao_imediata: string | null;
  afeta_trabalho_entregue: boolean | null;
  causa_raiz: string | null;
  analisada_por_nome: string | null;
  analisada_em: string | null;
  eficacia_conclusao: string | null;
  eficacia_por_nome: string | null;
  eficacia_em: string | null;
  encerrada_por_nome: string | null;
  encerrada_em: string | null;
  motivo_cancelamento: string | null;
  os_id: string | null;
  os_numero: string | null;
  equipamento_codigo: string | null;
  cliente_nome: string | null;
  acoes: number;
  acoes_abertas: number;
  acoes_vencidas: number;
  falta_para_encerrar: string | null;
  dias_aberta: number | null;
}

interface Acao {
  id: string;
  tipo: string;
  descricao: string;
  responsavel_nome: string | null;
  prazo: string | null;
  concluida_em: string | null;
  concluida_por_nome: string | null;
  resultado: string | null;
  cancelada_em: string | null;
  vencida: boolean;
}

const TOM: Record<string, Tom> = {
  aberta: 'critico', em_analise: 'atencao', acao_definida: 'andamento',
  em_execucao: 'andamento', aguardando_eficacia: 'atencao',
  encerrada: 'ok', cancelada: 'neutro',
};

const SITUACAO: Record<string, string> = {
  aberta: 'Aberta', em_analise: 'Em análise', acao_definida: 'Ação definida',
  em_execucao: 'Em execução', aguardando_eficacia: 'Aguardando eficácia',
  encerrada: 'Encerrada', cancelada: 'Cancelada',
};

const TIPO: Record<string, string> = {
  trabalho_nao_conforme: 'Trabalho não conforme',
  reclamacao: 'Reclamação',
  desvio_interno: 'Desvio interno',
  achado_auditoria: 'Achado de auditoria',
  melhoria: 'Melhoria',
};

const TIPO_ACAO: Record<string, string> = {
  correcao: 'Correção', corretiva: 'Ação corretiva',
  preventiva: 'Ação preventiva',
};

const GRAVIDADE: Record<string, string> = {
  baixa: 'Baixa', media: 'Média', alta: 'Alta',
};

// ── a lista ──────────────────────────────────────────────────────────

export function NaoConformidades() {
  const [linhas, setLinhas] = useState<Nc[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [tipo, setTipo] = useState<string | null>(null);
  const [soAbertas, setSoAbertas] = useState(true);
  const [abrindo, setAbrindo] = useState(false);
  /* A barra do padrão (onda 273): busca, gravidade, período e o resumo. */
  const [busca, setBusca] = useState('');
  const [gravidade, setGravidade] = useState<string | null>(null);
  const [recorte, setRecorte] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState<ValorDoPeriodo>({ de: '', ate: '' });
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try {
      const q = new URLSearchParams();
      if (tipo) q.set('tipo', tipo);
      if (soAbertas) q.set('abertas', '1');
      setLinhas(await api<Nc[]>(`/nao-conformidades?${q}`));
    } catch (e) { setErro(e); }
  }, [tipo, soAbertas]);

  useEffect(() => { void carregar(); }, [carregar]);

  const RECORTES = [
    { chave: 'vencidas', rotulo: 'Ação fora do prazo', aux: 'ação corretiva vencida', tom: 'critico' as const,
      f: (n: Nc) => n.acoes_vencidas > 0 },
    { chave: 'alta', rotulo: 'Gravidade alta', aux: 'em aberto', tom: 'critico' as const,
      f: (n: Nc) => n.gravidade === 'alta' && !['encerrada', 'cancelada'].includes(n.situacao) },
    { chave: 'eficacia', rotulo: 'Aguardando eficácia', aux: 'falta verificar se resolveu', tom: 'atencao' as const,
      f: (n: Nc) => n.situacao === 'aguardando_eficacia' },
    { chave: 'sem_acao', rotulo: 'Sem ação definida', aux: 'aberta ou em análise', tom: 'atencao' as const,
      f: (n: Nc) => ['aberta', 'em_analise'].includes(n.situacao) },
  ];
  const rec = RECORTES.find((r) => r.chave === recorte) ?? null;
  const filtradas = useMemo(() => {
    const b = busca.trim().toLowerCase();
    return (linhas ?? []).filter((n) => {
      if (rec && !rec.f(n)) return false;
      if (gravidade && n.gravidade !== gravidade) return false;
      const dia = n.detectada_em ? n.detectada_em.slice(0, 10) : null;
      if (periodo.de && (!dia || dia < periodo.de)) return false;
      if (periodo.ate && (!dia || dia > periodo.ate)) return false;
      if (b && ![n.numero, n.titulo, n.os_numero, n.equipamento_codigo, n.cliente_nome]
        .filter(Boolean).join(' ').toLowerCase().includes(b)) return false;
      return true;
    });
  }, [linhas, rec, gravidade, periodo.de, periodo.ate, busca]);
  const pag = usePaginacao(filtradas);
  const ativos: FiltroAtivo[] = [];
  if (rec) ativos.push({ chave: 'rec', texto: rec.rotulo, tirar: () => setRecorte(null) });
  if (tipo) ativos.push({ chave: 'tp', texto: `Tipo: ${TIPO[tipo] ?? tipo}`, tirar: () => setTipo(null) });
  if (gravidade) ativos.push({ chave: 'gr', texto: `Gravidade: ${GRAVIDADE[gravidade]}`, tirar: () => setGravidade(null) });
  if (busca) ativos.push({ chave: 'q', texto: `Busca: ${busca}`, tirar: () => setBusca('') });
  if (periodo.de || periodo.ate) {
    ativos.push({ chave: 'per', texto: `Detectadas: ${fmtData(periodo.de || null) ?? '…'} – ${fmtData(periodo.ate || null) ?? '…'}`, tirar: () => setPeriodo({ de: '', ate: '' }) });
  }

  return (
    <div>
      <div className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Não conformidades</h1>
          <p className="sub">
            Trabalho não conforme, reclamações e melhorias — o caminho é o
            mesmo. As abertas e as de gravidade alta primeiro.
          </p>
        </div>
        {pode('relato.registrar') && (
          <button type="button" className="botao"
            onClick={() => setAbrindo(true)}>
            Registrar
          </button>
        )}
      </div>

      {linhas && (
        <ResumoQueFiltra rotulo="Resumo das não conformidades" ativo={recorte} aoEscolher={setRecorte}
          itens={RECORTES.map((r) => ({ chave: r.chave, rotulo: r.rotulo, aux: r.aux, tom: r.tom,
            numero: linhas.filter(r.f).length }))} />
      )}

      {/*
        * A barra do padrão (onda 273): o tipo era um controle segmentado e
        * «Só as que não foram encerradas» uma caixa de seleção solta;
        * ganham busca, gravidade e o período pela data de detecção.
        */}
      <BarraDeLista rotulo="Filtros das não conformidades">
        <BuscaDaBarra valor={busca} aoMudar={setBusca}
          placeholder="Número, título, OS, equipamento ou cliente" rotulo="Buscar não conformidade" />
        <Periodo valor={periodo} aoMudar={setPeriodo} rotulo="Detectadas"
          atalhos={['semana', 'mes', 'mesPassado', 'ultimos30', 'tudo']} />
        <Seletor naBarra rotulo="Tipo" valor={tipo} aoMudar={setTipo} todos="Todos os tipos"
          opcoes={Object.entries(TIPO).map(([valor, texto]) => ({ valor, texto }))} />
        <Seletor naBarra rotulo="Gravidade" valor={gravidade} aoMudar={setGravidade} todos="Todas"
          opcoes={Object.entries(GRAVIDADE).map(([valor, texto]) => ({ valor, texto }))} />
        <AlternarDaBarra ligado={soAbertas} aoMudar={setSoAbertas}>
          {soAbertas ? '✓ Só as não encerradas' : 'Com as encerradas'}
        </AlternarDaBarra>
      </BarraDeLista>
      <FiltrosAtivos itens={ativos}
        aoLimpar={() => { setRecorte(null); setTipo(null); setGravidade(null); setBusca(''); setPeriodo({ de: '', ate: '' }); }}
        contagem={linhas ? `${filtradas.length} de ${linhas.length}` : undefined} />

      {erro ? <ErroDaApi erro={erro} />
        : !linhas ? <Carregando />
        : (
          <div className="cartao">
            {filtradas.length === 0 ? (
              <Vazio>
                {linhas.length > 0 || ativos.length > 0
                  ? 'Nenhuma não conformidade com estes filtros. Use «Limpar filtros».'
                  : 'Nenhuma registrada. Um laboratório sem não conformidade registrada não é um laboratório sem problema — é um que não olha.'}
              </Vazio>
            ) : (
            <div className="rola">
              <table className="dados">
                <thead>
                  <tr>
                    <th scope="col">Número</th>
                    <th scope="col">O quê</th>
                    <th scope="col">Tipo</th>
                    <th scope="col">Ações</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {pag.visiveis.map((n) => (
                    <tr key={n.id}
                      className={n.acoes_vencidas > 0 ? 'atencao' : undefined}>
                      <th scope="row">
                        <Link to={`/nao-conformidades/${n.id}`}>
                          <span className="mono">{n.numero}</span>
                        </Link>
                        <span className="sub">{fmtData(n.detectada_em)}</span>
                      </th>
                      <td>
                        {n.titulo}
                        <span className="sub">
                          {[n.os_numero, n.equipamento_codigo, n.cliente_nome]
                            .filter(Boolean).join(' · ')}
                        </span>
                      </td>
                      <td>
                        {TIPO[n.tipo] ?? n.tipo}
                        {n.gravidade === 'alta' && (
                          <span className="sub">gravidade alta</span>
                        )}
                      </td>
                      <td>
                        {n.acoes === 0
                          ? <span className="nada">nenhuma</span>
                          : `${n.acoes - n.acoes_abertas}/${n.acoes} concluída(s)`}
                        {n.acoes_vencidas > 0 && (
                          <span className="sub">
                            {n.acoes_vencidas} fora do prazo
                          </span>
                        )}
                      </td>
                      <td>
                        <Selo tom={TOM[n.situacao] ?? 'neutro'}>
                          {SITUACAO[n.situacao] ?? n.situacao}
                        </Selo>
                        {n.falta_para_encerrar && (
                          <span className="sub">
                            falta {n.falta_para_encerrar}
                          </span>
                        )}
                        {n.dias_aberta !== null && n.dias_aberta > 0 && (
                          <span className="sub">
                            aberta há {n.dias_aberta} dia(s)
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            <RodapeDaLista {...pag} teto={400} ordem="abertas, gravidade, detectada ↓" />
          </div>
        )}

      {abrindo && (
        <DialogoAbrir aoFechar={() => setAbrindo(false)}
          aoPronto={() => { setAbrindo(false); void carregar(); }} />
      )}
    </div>
  );
}

function DialogoAbrir({ aoFechar, aoPronto }: {
  aoFechar(): void; aoPronto(): void;
}) {
  const [tipo, setTipo] = useState<string | null>('trabalho_nao_conforme');
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [gravidade, setGravidade] = useState<string | null>('media');
  const [acaoImediata, setAcaoImediata] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      await api('/nao-conformidades', {
        metodo: 'POST',
        corpo: {
          tipo, titulo, descricao, gravidade,
          acaoImediata: acaoImediata.trim() || undefined,
        },
      });
      aoPronto();
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Registrar não conformidade" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <Escolha rotulo="Tipo" valor={tipo} limpavel={false}
        aoEscolher={(v) => setTipo(v ?? 'trabalho_nao_conforme')}
        opcoes={Object.entries(TIPO).map(([valor, texto]) => ({
          valor, texto }))} />
      <Campo rotulo="Título" value={titulo}
        dica="Como aparece na lista — que se entenda sem abrir"
        onChange={(ev) => setTitulo(ev.target.value)} />
      <label className="campo">
        <span className="rotulo">O que aconteceu</span>
        <textarea rows={4} value={descricao}
          onChange={(ev) => setDescricao(ev.target.value)}
          placeholder="quem lê isto seis meses depois não estava lá…" />
      </label>
      <Escolha rotulo="Gravidade" valor={gravidade} limpavel={false}
        aoEscolher={(v) => setGravidade(v ?? 'media')}
        opcoes={Object.entries(GRAVIDADE).map(([valor, texto]) => ({
          valor, texto }))} />
      <label className="campo">
        <span className="rotulo">Ação imediata (opcional)</span>
        <textarea rows={2} value={acaoImediata}
          onChange={(ev) => setAcaoImediata(ev.target.value)}
          placeholder="o que foi feito na hora para conter o estrago" />
      </label>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || titulo.trim().length < 5
            || descricao.trim().length < 20}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Registrar'}
        </button>
      </div>
    </Dialogo>
  );
}

// ── a ficha ──────────────────────────────────────────────────────────

export function NaoConformidadeFicha() {
  const { id = '' } = useParams();
  const [d, setD] = useState<{ nc: Nc; acoes: Acao[] } | null>(null);
  useCaminho(d ? [{ rotulo: d.nc.titulo, codigo: d.nc.numero }] : null);
  const [erro, setErro] = useState<unknown>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [passo, setPasso] = useState<'causa' | 'acao' | 'eficacia' | null>(null);
  const [cancelando, setCancelando] = useState(false);
  const { pode } = useSessao();

  const carregar = useCallback(async () => {
    setErro(null);
    try { setD(await api(`/nao-conformidades/${id}`)); } catch (e) { setErro(e); }
  }, [id]);

  useEffect(() => { void carregar(); }, [carregar]);

  if (erro) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Abrindo a não conformidade" />;

  const { nc, acoes } = d;
  const viva = !['encerrada', 'cancelada'].includes(nc.situacao);
  const podeAgir = pode('relato.triagem') && viva;

  const encerrar = async () => {
    setOcupado(true); setAviso(null);
    try {
      setD(await api(`/nao-conformidades/${id}/encerrar`,
        { metodo: 'POST', corpo: {} }));
    } catch (e) { setAviso(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <div>
      <div className="linha-topo">
        <div className="cresce">
          <h1><span className="mono">{nc.numero}</span> · {nc.titulo}</h1>
          <p className="sub">
            {TIPO[nc.tipo] ?? nc.tipo} · detectada em{' '}
            {fmtData(nc.detectada_em)} por {nc.detectada_por_nome}
            {nc.os_numero && <> · <Link to={`/os/${nc.os_id}`}>{nc.os_numero}</Link></>}
          </p>
        </div>
        <Selo tom={TOM[nc.situacao] ?? 'neutro'}>
          {SITUACAO[nc.situacao] ?? nc.situacao}
        </Selo>
      </div>

      {aviso && <Aviso tipo="erro">{aviso}</Aviso>}

      {nc.falta_para_encerrar && (
        <Aviso tipo="atencao" titulo="Para encerrar, falta">
          {nc.falta_para_encerrar}. A escada é a da norma (17025 §8.7), e
          quem a cobra é o banco — esta frase vem de lá, e não de uma
          segunda conta feita nesta tela.
        </Aviso>
      )}

      <section className="bloco">
        <h2>1 · O que aconteceu</h2>
        <p style={{ whiteSpace: 'pre-wrap' }}>{nc.descricao}</p>
        {nc.acao_imediata && (
          <>
            <h3>Ação imediata</h3>
            <p style={{ whiteSpace: 'pre-wrap' }}>{nc.acao_imediata}</p>
          </>
        )}
        {nc.afeta_trabalho_entregue && (
          <Aviso tipo="atencao" titulo="Afeta trabalho já entregue">
            A 17025 §7.10.1 pede que o cliente seja avisado quando o
            trabalho já saiu da casa.
          </Aviso>
        )}
      </section>

      <section className="bloco">
        <h2>
          2 · A causa
          <span className="estado-do-bloco">
            {nc.causa_raiz ? `analisada por ${nc.analisada_por_nome}`
              : 'não analisada'}
          </span>
        </h2>
        {nc.causa_raiz
          ? <p style={{ whiteSpace: 'pre-wrap' }}>{nc.causa_raiz}</p>
          : (
            <p className="sub">
              «Erro do operador» não é causa — é onde ela apareceu. A norma
              pede a causa que, tratada, impede o problema de voltar
              (§8.7.2 a).
            </p>
          )}
        {podeAgir && (
          <div className="acoes">
            <button type="button" onClick={() => setPasso('causa')}>
              {nc.causa_raiz ? 'Revisar a causa' : 'Registrar a causa'}
            </button>
          </div>
        )}
      </section>

      <section className="bloco">
        <h2>
          3 · As ações
          <span className="estado-do-bloco">
            {acoes.length === 0 ? 'nenhuma'
              : `${acoes.filter((a) => a.concluida_em).length} de ${
                acoes.length} concluída(s)`}
          </span>
        </h2>
        <p className="sub">
          A <b>correção</b> conserta o caso; a <b>ação corretiva</b> mexe na
          causa para o caso não voltar. Juntá-las produz relatórios em que
          toda não conformidade foi resolvida refazendo o ensaio.
        </p>
        {acoes.length > 0 && (
          <div className="rola">
            <table>
              <thead>
                <tr>
                  <th scope="col">O quê</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Responsável</th>
                  <th scope="col">Prazo</th>
                  <th scope="col">Situação</th>
                </tr>
              </thead>
              <tbody>
                {acoes.map((a) => (
                  <tr key={a.id} className={a.vencida ? 'atencao' : undefined}>
                    <th scope="row">
                      {a.descricao}
                      {a.resultado && <span className="sub">{a.resultado}</span>}
                    </th>
                    <td>{TIPO_ACAO[a.tipo] ?? a.tipo}</td>
                    <td>{a.responsavel_nome ?? '—'}</td>
                    <td>
                      {a.prazo ? fmtData(a.prazo)
                        : <span className="nada">sem prazo</span>}
                    </td>
                    <td>
                      {a.concluida_em ? (
                        <Selo tom="ok">
                          Concluída {fmtData(a.concluida_em)}
                        </Selo>
                      ) : a.cancelada_em ? (
                        <Selo tom="neutro">Cancelada</Selo>
                      ) : (
                        <>
                          <Selo tom={a.vencida ? 'critico' : 'andamento'}>
                            {a.vencida ? 'fora do prazo' : 'em aberto'}
                          </Selo>
                          {podeAgir && (
                            <ConcluirAcao ncId={id} acaoId={a.id}
                              aoPronto={setD} />
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {podeAgir && (
          <div className="acoes">
            <button type="button" onClick={() => setPasso('acao')}>
              Definir uma ação
            </button>
          </div>
        )}
      </section>

      <section className="bloco">
        <h2>
          4 · A eficácia
          <span className="estado-do-bloco">
            {nc.eficacia_conclusao
              ? `verificada por ${nc.eficacia_por_nome}` : 'não verificada'}
          </span>
        </h2>
        {nc.eficacia_conclusao
          ? <p style={{ whiteSpace: 'pre-wrap' }}>{nc.eficacia_conclusao}</p>
          : (
            <p className="sub">
              A ação foi feita; alguém precisa dizer se ela <b>resolveu</b>
              {' '}(§8.7.3). É o degrau que todo sistema da qualidade
              esquece, porque é o único que acontece semanas depois.
            </p>
          )}
        {podeAgir && (
          <div className="acoes">
            <button type="button" onClick={() => setPasso('eficacia')}>
              Verificar a eficácia
            </button>
          </div>
        )}
      </section>

      <section className="bloco">
        <h2>5 · Encerramento</h2>
        {nc.encerrada_em ? (
          <p>
            Encerrada em {fmtData(nc.encerrada_em)} por{' '}
            {nc.encerrada_por_nome}.
          </p>
        ) : nc.motivo_cancelamento ? (
          <p>Cancelada: {nc.motivo_cancelamento}</p>
        ) : (
          <>
            <p className="sub">
              Encerrar é assinar — fica quem encerrou e quando.
            </p>
            {podeAgir && (
              <div className="acoes">
                <button type="button" className="principal" disabled={ocupado}
                  onClick={() => { void encerrar(); }}>
                  {ocupado ? 'Encerrando…' : 'Encerrar'}
                </button>
                {/*
                  Cancelar não é encerrar: encerrar diz que o problema foi
                  tratado; cancelar diz que ele não era um problema — abriu
                  duplicada, ou o que se viu não aconteceu. Juntar os dois
                  faria a lista de encerradas contar como resolvido o que
                  ninguém tratou.
                */}
                <button type="button" onClick={() => setCancelando(true)}>
                  Cancelar o registro
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {cancelando && (
        <DialogoCancelar ncId={id} aoFechar={() => setCancelando(false)}
          aoPronto={(nova) => { setD(nova); setCancelando(false); }} />
      )}

      {passo && (
        <DialogoPasso passo={passo} ncId={id}
          aoFechar={() => setPasso(null)}
          aoPronto={(nova) => { setD(nova); setPasso(null); }} />
      )}
    </div>
  );
}

function ConcluirAcao({ ncId, acaoId, aoPronto }: {
  ncId: string; acaoId: string; aoPronto(d: { nc: Nc; acoes: Acao[] }): void;
}) {
  const [aberto, setAberto] = useState(false);
  const [resultado, setResultado] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      aoPronto(await api(
        `/nao-conformidades/${ncId}/acoes/${acaoId}/concluir`,
        { metodo: 'POST', corpo: { resultado } }));
      setAberto(false);
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <>
      <button type="button" className="ligacao" onClick={() => setAberto(true)}>
        concluir
      </button>
      {aberto && (
        <Dialogo aberto titulo="Concluir a ação" aoFechar={() => setAberto(false)}>
          {erro && <Aviso tipo="erro">{erro}</Aviso>}
          <label className="campo">
            <span className="rotulo">O que aconteceu quando foi feita?</span>
            <textarea rows={3} value={resultado}
              onChange={(ev) => setResultado(ev.target.value)} />
          </label>
          <div className="acoes">
            <button type="button" onClick={() => setAberto(false)}>
              Cancelar
            </button>
            <button type="button" className="principal"
              disabled={ocupado || resultado.trim().length < 10}
              onClick={() => { void gravar(); }}>
              {ocupado ? 'Gravando…' : 'Concluir'}
            </button>
          </div>
        </Dialogo>
      )}
    </>
  );
}

function DialogoPasso({ passo, ncId, aoFechar, aoPronto }: {
  passo: 'causa' | 'acao' | 'eficacia';
  ncId: string;
  aoFechar(): void;
  aoPronto(d: { nc: Nc; acoes: Acao[] }): void;
}) {
  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState<string | null>('corretiva');
  const [responsavel, setResponsavel] = useState<string | null>(null);
  const [prazo, setPrazo] = useState('');
  const [gente, setGente] = useState<{ id: string; nome: string }[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  useEffect(() => {
    if (passo !== 'acao') return;
    api<{ id: string; nome: string }[]>('/funcionarios')
      .then(setGente).catch(() => setGente([]));
  }, [passo]);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      if (passo === 'causa') {
        aoPronto(await api(`/nao-conformidades/${ncId}/causa`,
          { metodo: 'POST', corpo: { causaRaiz: texto } }));
      } else if (passo === 'eficacia') {
        aoPronto(await api(`/nao-conformidades/${ncId}/eficacia`,
          { metodo: 'POST', corpo: { conclusao: texto } }));
      } else {
        aoPronto(await api(`/nao-conformidades/${ncId}/acoes`, {
          metodo: 'POST',
          corpo: {
            tipo, descricao: texto, responsavelId: responsavel,
            prazo: prazo || undefined,
          },
        }));
      }
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  const titulo = passo === 'causa' ? 'A causa'
    : passo === 'eficacia' ? 'A eficácia' : 'Definir uma ação';

  const minimo = passo === 'acao' ? 10 : 20;
  const pronto = texto.trim().length >= minimo
    && (passo !== 'acao' || !!responsavel);

  return (
    <Dialogo aberto titulo={titulo} aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {passo === 'acao' && (
        <>
          <Escolha rotulo="Tipo" valor={tipo} limpavel={false}
            aoEscolher={(v) => setTipo(v ?? 'corretiva')}
            opcoes={Object.entries(TIPO_ACAO).map(([valor, texto2]) => ({
              valor, texto: texto2 }))} />
          <Escolha rotulo="Responsável" valor={responsavel}
            aoEscolher={setResponsavel}
            opcoes={gente.map((g) => ({ valor: g.id, texto: g.nome }))} />
          <Campo rotulo="Prazo (opcional)" type="date" value={prazo}
            onChange={(ev) => setPrazo(ev.target.value)} />
        </>
      )}
      <label className="campo">
        <span className="rotulo">
          {passo === 'causa' ? 'Qual é a causa?'
            : passo === 'eficacia' ? 'A ação resolveu?'
              : 'O que vai ser feito?'}
        </span>
        <textarea rows={4} value={texto}
          onChange={(ev) => setTexto(ev.target.value)}
          placeholder={passo === 'causa'
            ? '«erro do operador» é onde a causa apareceu, não a causa…'
            : passo === 'eficacia'
              ? 'os três ciclos seguintes ficaram dentro da faixa…'
              : 'incluir a estufa na campanha de calibração interna…'} />
      </label>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Cancelar</button>
        <button type="button" className="principal"
          disabled={ocupado || !pronto}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Gravar'}
        </button>
      </div>
    </Dialogo>
  );
}

/**
 * Cancelar o registro — que não é encerrar.
 *
 * Encerrar diz que o problema foi tratado; cancelar diz que ele não era
 * um problema: abriu duplicada, ou o que se viu não aconteceu. Juntar os
 * dois faria a lista de encerradas contar como resolvido o que ninguém
 * tratou — e é essa lista que vai para a auditoria.
 */
function DialogoCancelar({ ncId, aoFechar, aoPronto }: {
  ncId: string; aoFechar(): void; aoPronto(d: { nc: Nc; acoes: Acao[] }): void;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const gravar = async () => {
    setOcupado(true); setErro(null);
    try {
      aoPronto(await api(`/nao-conformidades/${ncId}/cancelar`,
        { metodo: 'POST', corpo: { motivo } }));
    } catch (e) { setErro(mensagemDoErro(e)); } finally { setOcupado(false); }
  };

  return (
    <Dialogo aberto titulo="Cancelar o registro" aoFechar={aoFechar}>
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <p className="sub">
        Cancelar não é encerrar. Use isto quando o registro <b>não era</b>
        {' '}uma não conformidade — duplicado, ou o que se viu não
        aconteceu. O registro continua na base, cancelado e com o motivo.
      </p>
      <label className="campo">
        <span className="rotulo">Por quê?</span>
        <textarea rows={3} value={motivo}
          onChange={(ev) => setMotivo(ev.target.value)}
          placeholder="duplicada da NC-2026-004; o desvio era do padrão, não da estufa…" />
      </label>
      <div className="acoes">
        <button type="button" onClick={aoFechar}>Voltar</button>
        <button type="button" className="principal"
          disabled={ocupado || motivo.trim().length < 10}
          onClick={() => { void gravar(); }}>
          {ocupado ? 'Gravando…' : 'Cancelar o registro'}
        </button>
      </div>
    </Dialogo>
  );
}
